import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, InputNumber, Table, Tag, Tooltip, message } from 'antd';
import { API } from '@/services/api.service';
import { TABLE4_FIELDS, date, errorText, money } from './format';

/**
 * Table 4: the capital at the end of the period. Each field is read from the
 * AB ledger by the account rules in the settings; a number typed in here wins
 * over the ledger (a company whose books are not on the platform, or a
 * correction). Amounts are entered in RSD; the form gets USD at the NBS
 * middle rate of the last day of the period.
 */
export default function CapitalTab({
  data,
  companyId,
  period,
  locked,
  onSaved,
}) {
  const { t } = useTranslation('diReports');
  const [manual, setManual] = useState({});

  useEffect(() => {
    setManual({ ...(data.report?.table4Manual || {}) });
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      API.diReports.saveReport(companyId, period, { table4Manual: manual }),
    onSuccess: (fresh) => {
      message.success(t('messages.saved'));
      onSaved(fresh);
    },
    onError: async (e) => message.error(await errorText(e)),
  });

  const { rate } = data;
  const effective = (key) =>
    manual[key] !== null && manual[key] !== undefined
      ? manual[key]
      : (data.table4Ledger?.[key] ?? null);
  const usd = (key) => {
    const v = effective(key);
    return v === null || !rate ? null : Math.round((v / rate) * 100) / 100;
  };
  const rulesText = (key) =>
    (data.table4Rules?.[key] || [])
      .map((r) => `${r.sign === -1 ? '−' : '+'}${r.prefix}*`)
      .join('  ');

  const total = TABLE4_FIELDS.reduce(
    (s, k) => s + (k === 'f31' ? -1 : 1) * (usd(k) || 0),
    0
  );

  const columns = [
    {
      title: t('capital.field'),
      dataIndex: 'key',
      render: (key) => (
        <span>
          {t(`table4.${key}`)}{' '}
          <Tooltip title={t('capital.rules', { rules: rulesText(key) })}>
            <InfoCircleOutlined className="opacity-50" />
          </Tooltip>
        </span>
      ),
    },
    {
      title: t('capital.fromAb'),
      key: 'ab',
      align: 'right',
      width: 170,
      render: (_, { key }) =>
        data.table4Ledger ? (
          money(data.table4Ledger[key])
        ) : (
          <Tag bordered={false}>{t('capital.noAb')}</Tag>
        ),
    },
    {
      title: t('capital.manual'),
      key: 'manual',
      width: 200,
      render: (_, { key }) => (
        <InputNumber
          disabled={locked}
          value={manual[key]}
          placeholder={t('capital.manualPlaceholder')}
          onChange={(v) => setManual((m) => ({ ...m, [key]: v ?? null }))}
          style={{ width: '100%' }}
          decimalSeparator=","
          step={0.01}
        />
      ),
    },
    {
      title: t('capital.usd'),
      key: 'usd',
      align: 'right',
      width: 150,
      render: (_, { key }) => <strong>{money(usd(key))}</strong>,
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm">
        {t('capital.rateLine', {
          date: date(data.rateDate),
          rate: rate ? money(rate, 4) : '—',
        })}
        {data.ledger && (
          <span className="ml-3 opacity-70">
            {t('capital.ledgerLine', {
              last: date(data.ledger.lastEntryDate),
              pulled: date(data.ledger.extractedAt),
            })}
          </span>
        )}
      </div>
      {!data.table4Ledger && (
        <Alert
          type="info"
          showIcon
          className="!static"
          message={t('capital.noAbHelp')}
        />
      )}
      <Table
        rowKey="key"
        size="small"
        pagination={false}
        columns={columns}
        dataSource={TABLE4_FIELDS.map((key) => ({ key }))}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={3}>
              <strong>{t('table4.total')}</strong>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <strong>{money(total)}</strong>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      <div className="text-xs opacity-70">{t('capital.help')}</div>
      {!locked && (
        <div className="flex justify-end">
          <Button
            shape="round"
            type="primary"
            icon={<SaveOutlined />}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('actions.save')}
          </Button>
        </div>
      )}
    </div>
  );
}
